import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Shield, AlertCircle, HelpCircle } from 'lucide-react';
import Navigation from '../components/Navigation';

const SMSTermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      {/* Header */}
      <Navigation />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <MessageSquare className="w-8 h-8 text-brand-blue mr-3" />
                <h1 className="text-3xl font-bold text-gray-900">SMS Terms and Conditions</h1>
              </div>
              <p className="text-gray-600 leading-relaxed">
                <strong>Last Updated:</strong> December 15, 2024
              </p>
              <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p className="text-blue-800">
                  <strong>Important:</strong> By providing your phone number and opting in to SMS communications, 
                  you agree to receive text messages from MasterJi for verification and security purposes. 
                  Please read these terms carefully.
                </p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. SMS Service Overview</h2>
              <p className="text-gray-700 mb-4">
                MasterJi uses SMS (Short Message Service) to send verification codes and security notifications 
                to your mobile phone. This service helps protect your account and ensure secure access to our platform.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">1.1 Types of SMS Messages</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Verification Codes:</strong> One-time codes for account verification and password reset</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Security Alerts:</strong> Important security notifications about your account</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Account Updates:</strong> Critical account-related information</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Service Notifications:</strong> Important updates about our services</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Consent and Opt-in Requirements</h2>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-yellow-800">
                    <strong>Explicit Consent Required:</strong> You must explicitly opt-in to receive SMS messages. 
                    By checking the SMS consent box during registration, you agree to these terms.
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Consent Requirements</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Clear Purpose:</strong> SMS messages are sent for verification and security purposes only</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Explicit Opt-in:</strong> You must actively consent to receive SMS messages</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Frequency:</strong> Messages are sent only when necessary for account verification</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Opt-out Rights:</strong> You can opt-out at any time by replying STOP</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. SMS Message Examples</h2>
              
              <div className="space-y-4">
                <div className="bg-gray-100 p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="font-semibold text-gray-800 mb-2">Verification Code:</h4>
                  <p className="text-gray-700 font-mono bg-white p-2 rounded border">
                    "Your MasterJi verification code is: 123456. This code expires in 10 minutes."
                  </p>
                </div>

                <div className="bg-gray-100 p-4 rounded-lg border-l-4 border-orange-500">
                  <h4 className="font-semibold text-gray-800 mb-2">Security Alert:</h4>
                  <p className="text-gray-700 font-mono bg-white p-2 rounded border">
                    "MasterJi Security Alert: New login detected from unknown device. If this wasn't you, please secure your account immediately."
                  </p>
                </div>

                <div className="bg-gray-100 p-4 rounded-lg border-l-4 border-blue-500">
                  <h4 className="font-semibold text-gray-800 mb-2">Account Update:</h4>
                  <p className="text-gray-700 font-mono bg-white p-2 rounded border">
                    "MasterJi: Your account password has been successfully changed. If you didn't make this change, contact support immediately."
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Opt-out Instructions</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 How to Opt-out</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Reply STOP:</strong> Text STOP to any MasterJi SMS to unsubscribe</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Reply HELP:</strong> Text HELP to any MasterJi SMS for assistance</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Account Settings:</strong> Update your communication preferences in your account</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Contact Support:</strong> Email us at infotherealmasterji@gmail.com</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Legal Compliance</h2>
              
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-green-800 mb-2">TCPA Compliance</h3>
                    <ul className="text-green-700 space-y-1">
                      <li>• MasterJi complies with the Telephone Consumer Protection Act (TCPA)</li>
                      <li>• We maintain records of SMS consent and opt-out requests</li>
                      <li>• SMS messages are sent only to users who have explicitly opted in</li>
                      <li>• We honor all opt-out requests immediately</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 p-6 bg-gray-50 rounded-xl"
            >
              <div className="flex items-start">
                <HelpCircle className="w-6 h-6 text-brand-blue mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Us</h3>
                  <p className="text-gray-600 mb-4">
                    If you have any questions about these SMS Terms and Conditions, please contact us:
                  </p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Email:</strong> infotherealmasterji@gmail.com</p>
                    <p><strong>SMS Help:</strong> Reply HELP to any MasterJi SMS</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="mt-8 text-center text-sm text-gray-500">
              <p>These SMS Terms and Conditions were last updated on December 15, 2024.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SMSTermsPage;
